import {useEffect, useMemo, useState} from 'react';
import {
  getKeyFromPath,
  getUbaPageName,
  handleTracking,
  openUrl,
  replacePlaceholders,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {Box, Card} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useSelector, shallowEqual} from 'react-redux';

import type {
  AppState,
  AdsElementType,
  AdsEventInfo,
} from '@jobseeker/common-core/types';
import type {AdElement} from '../types';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import {useInView} from '@jobseeker/lotus/hooks/observer';

const ALLOWED_PAGES_FOR_GOLD_SACH = ['jobfeed', 'category', 'tag', 'jobdetail'];

type AdsBannerType = {
  adParams: AdElement;
  adType?: string;
  adsEventInfo?: AdsEventInfo;
};
export const AdBannerComponent = ({
  adParams,
  adsEventInfo,
  adType = 'normal',
}: AdsBannerType) => {
  const {slot} = adParams;
  const [eventAdsBanner, setEventAdsBanner] = useState(Array<AdsElementType>);

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const {page} = useSelector((state: AppState) => state.app);

  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory
  );
  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );

  if (
    adType === 'tempGoldSachBanner' &&
    !ALLOWED_PAGES_FOR_GOLD_SACH.includes(page)
  ) {
    return;
  }

  const advertisementBannerInfo = useSelector(
    (state: AppState) => state.ads.advertisementBanner,
    shallowEqual
  );

  useLoadAds(
    slot,
    setEventAdsBanner,
    {
      categoryIds: categoryId,
      ...(!isAppIimjobs &&
        adType === 'tempGoldSachBanner' && {
          onRotation: 1,
        }),
    },
    false,
    !advertisementBannerInfo[0]?.imageUrl
  );

  const loadedBannerAdsInfo = advertisementBannerInfo[0]?.title
    ? advertisementBannerInfo
    : eventAdsBanner;

  const adsList = useMemo(() => {
    return loadedBannerAdsInfo?.filter(adItem => {
      if (adType === 'tempGoldSachBanner') {
        const date = new Date().getDate();
        if (adItem?.ordering === date.toString()) {
          return !!adItem['imageUrl'];
        }
      } else {
        return !!adItem['imageUrl'];
      }
    });
  }, [loadedBannerAdsInfo]);

  const callEvent = (eventName: string, title: string) => {
    if (eventName) {
      const category = replacePlaceholders(
        adsEventInfo && adsEventInfo['eventCategory']
          ? adsEventInfo['eventCategory']
          : '',
        {
          categoryId,
        }
      );

      handleTracking(eventName, {
        event_category: category,
        Source: page,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
        ...(title ? {Company: title} : {}),
      });
    }
  };

  const getEventName = (title: string, type: 'view' | 'click') => {
    return adsEventInfo && adsEventInfo[type]
      ? replacePlaceholders(adsEventInfo[type], {page, categoryId, title})
      : '';
  };

  const {ref, isInView} = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const [hasTracked, setHasTracked] = useState(false);

  useEffect(() => {
    if (
      (!categoryId && adType !== 'tempGoldSachBanner' && page !== 'jobfeed') ||
      !adsList?.length
    )
      return;

    adsList.forEach(ads => {
      callEvent(getEventName(ads?.title, 'view'), ads?.title);
    });

    if (!hasTracked && isInView) {
      const timer = setTimeout(() => {
        adsList.forEach(ads => {
          TrackEventUBA('adBannerView', 'view', {
            ref: getKeyFromPath('ref') || '',
            page: getUbaPageName(),
            categoryID: page === 'category' ? categoryId : undefined,
            tagID: page === 'tag' ? Number(tagId) : undefined,
            companyId: ads.companyId || '',
            companyName: ads.title || '',
            sessionUsername: sessionStorage.getItem('userId') || '',
          });
        });

        setHasTracked(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [categoryId, adsList, isInView, hasTracked]);

  const handleClick = (ad: AdsElementType) => {
    openUrl(ad['link'], '_blank');
    callEvent(getEventName(ad?.title, 'click'), ad?.title);
    TrackEventUBA('adBannerClick', 'click', {
      ref: getKeyFromPath('ref') || '',
      page: getUbaPageName(),
      categoryID: page === 'category' ? categoryId : undefined,
      tagID: page === 'tag' ? Number(tagId) : undefined,
      companyId: ad.companyId || '',
      companyName: ad.title || '',
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
  };

  return (
    <>
      {adsList?.map((ad, index) => (
        <Card
          ref={ref}
          key={`advertisement-key-${index}`}
          onClick={() => handleClick(ad)}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            cursor: 'pointer',
            mt: index > 0 ? 1 : 0,
            mb:
              !isAppIimjobs &&
              adType === 'tempGoldSachBanner' &&
              adsList.length === 1
                ? '30px'
                : 0,
          }}
        >
          <Box
            width={'100%'}
            sx={{aspectRatio: '300/100'}}
            position={'relative'}
            data-testid="ad-banner"
          >
            <Image
              src={ad['imageUrl']}
              alt={`sponser_banner${index}`}
              quality={50}
              placeholder="blur"
              blurDataURL={ad['imageUrl']}
              layout="fill"
              objectFit="cover"
              rel="nofollow"
            />
          </Box>
        </Card>
      ))}
    </>
  );
};
